import React, { useState, useRef, useEffect } from "react";
import PersonIcon from "@mui/icons-material/Person";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import Badge from "@mui/material/Badge";
import MessageIcon from "@mui/icons-material/Message";
import NotificationsIcon from "@mui/icons-material/Notifications";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import { jwtDecode } from "jwt-decode";
import { useLoading } from "../../../../context/LoadingContext";
import { fetchData } from "../../../Requirement/services/RequirementServices";
import AlertModal from "../../../Requirement/components/common/AlerModel";
import { NotificationCenter } from "../../../../components/common/notifications";
import PeopleIcon from "@mui/icons-material/People";
import { Link } from "react-router-dom";

// Import the HelpModal component
import HelpModal from "../../../../services/HelpModalComponent";

const Header = ({ clientmenu }) => {
  const navigate = useNavigate();
  const [activeUser, setActiveUser] = useState("");
  const dropdownRef = useRef(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isArrowDown, setIsArrowDown] = useState(true);
  const [userMessageCount, setUserMessageCount] = useState(0);
  const [userKybInfo, setUserKybInfo] = useState([]);
  const [isProfileDropdownVisible, setIsProfileDropdownVisible] =
    useState(false);
  const [isNotificationDropdownVisible, setIsNotificationDropdownVisible] =
    useState(false);
  const [isHelpDropdownVisible, setIsHelpDropdownVisible] = useState(false);
  const [isHelpModalVisible, setIsHelpModalVisible] = useState(false);
  const { setLoading } = useLoading();
  const [isKybLoading, setIsKybLoading] = useState(true);
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
  });

  const Signout = async () => {
    setLoading(true);
    try {
      localStorage.clear();
      navigate("/login/buyer");
    } catch (error) {
      console.error("Error signing out: ", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);

        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_BUYERS")) {
          await Auth.signOut();
          navigate("/login/buyer");
        } else {
          setActiveUser(user.attributes.email);
        }
      } catch (error) {
        await Auth.signOut();
        navigate("/login/buyer");
      }
    };

    checkUser();
  }, [navigate]);

  const handleSubmit = () => {
    setModal({
      show: true,
      type: "warning",
      message: "Are you sure you want to log Out?",
      isConfirm: true,
      onConfirm: confirmLogout,
      onCancel: () => setModal((prev) => ({ ...prev, show: false })),
    });
  };

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  const confirmLogout = () => {
    setModal((prev) => ({ ...prev, show: false }));
    setModal({
      show: true,
      type: "success",
      title: "Success!",
      message: "Signed out successfully.",
      autoClose: 6000,
      nextAction: () => {
        Signout();
      },
    });
  };

  const toggleDropdown = () => {
    setIsDropdownVisible((prev) => !prev);
    setIsArrowDown((prev) => !prev);
    setIsNotificationDropdownVisible(false);
    setIsHelpDropdownVisible(false); // Close Help Dropdown when profile is clicked
  };

  const clientnav = () => {
    navigate("/my-clients");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownVisible(false);
        setIsArrowDown(true);
        setIsNotificationDropdownVisible(false);
        setIsProfileDropdownVisible(false);
        setIsHelpDropdownVisible(false); // Close Help Dropdown on outside click
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isHelpModalVisible) setIsHelpDropdownVisible(false); // Close dropdown when HelpModal opens
  }, [isHelpModalVisible]);

  useEffect(() => {
    const checkUserMessageCount = async () => {
      try {
        if (!activeUser) return;
        const msgResponse = await fetchData({
          target: "listMessages",
          userOrder: "CreatedAt",
          orderDirection: "ASC",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: {
            ReceiverUserID: activeUser,
            IsRead: 0,
          },
        });
        setUserMessageCount(msgResponse.total || 0);
      } catch (error) {
        console.error("Error fetching message count:", error);
        setUserMessageCount(0);
      }
    };

    const checkUserKYB = async () => {
      setIsKybLoading(true);
      try {
        if (!activeUser) return;
        const userKYBResponse = await fetchData({
          target: "listUserKYB",
          userOrder: "UserKybID",
          orderDirection: "ASC",
          rowStart: 0,
          pageSize: 1,
          filterTerms: { EmailID: activeUser },
        });
        if (userKYBResponse.items && userKYBResponse.items.length > 0) {
          setUserKybInfo(userKYBResponse.items[0] || null);
        } else {
          setUserKybInfo(null);
        }
      } catch (error) {
        console.error("Error fetching KYB info:", error);
        setUserKybInfo(null);
      } finally {
        setIsKybLoading(false);
      }
    };

    checkUserMessageCount();
    checkUserKYB();
  }, [activeUser]);

  const redirectToMessage = () => {
    navigate("/buyer/messaging");
  };

  const goToProfile = () => {
    navigate("/buyer/profile");
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownVisible((prev) => !prev);
    setIsNotificationDropdownVisible(false);
    setIsHelpDropdownVisible(false); // Close Help Dropdown when profile is clicked
  };

  // Help Dropdown menu items
  const menuItems = [
    {
      icon: <MenuBookOutlinedIcon style={{ color: "#4472C4" }} />,
      title: "Help",
      subtitle: "Get help from our self-serve repository",
      link: "#",
    },
    {
      icon: <ChatBubbleOutlineIcon style={{ color: "#F4B183" }} />,
      title: "Chat with us",
      subtitle: "24/7 support for platform and data query",
      link: "/buyer/messaging",
    },
    {
      icon: <EmailOutlinedIcon style={{ color: "#5B9BD5" }} />,
      title: "Email",
      subtitle: "support@agropreneurs.in",
      link: "/buyer/support",
    },
    {
      icon: <WhatsAppIcon style={{ color: "#70AD47" }} />,
      title: "WhatsApp",
      subtitle: "+91-xxxxxxxxxx",
      link: "https://wa.me/91111111111",
    },
  ];

  const openHelpModal = () => {
    setIsHelpDropdownVisible(false);
    setTimeout(() => setIsHelpModalVisible(true), 0);
  };

  return (
    <div className="header" ref={dropdownRef}>
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
        isConfirm={modal.isConfirm}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <header>
        <div className="header-cus">
          <img
            src="/images/logo.png"
            alt="Grains Global Logo"
            className="logo"
          />
        </div>
        <div className="diplay-flex-css">
          <div className="breadcrumbs">
            {/* <span
              className="kyb-link"
              onClick={() => navigate('/kyb-verification')}
              style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
            >
              Complete your KYB verification to proceed.
            </span> */}
          </div>

          {isKybLoading ? null : userKybInfo === null ||
            !userKybInfo?.Status ||
            userKybInfo.Status === "" ||
            userKybInfo.Status === "Pending" ? (
            <div className="breadcrumbs2">
              <span
                className="kyb-link"
                style={{
                  cursor: "pointer",
                  textDecoration: "none",
                }}
              >
                {userKybInfo === null ||
                !userKybInfo?.Status ||
                userKybInfo.Status === "" ? (
                  <>
                    Please complete your KYB verification to proceed.{" "}
                    <button
                      className="export-btn"
                      onClick={() => navigate("/buyer/setting")}
                    >
                      Click Here
                    </button>
                  </>
                ) : (
                  <>
                    KYB is under review.{" "}
                    <button
                      className="export-btn"
                      onClick={() => navigate("/buyer/setting")}
                    >
                      View Here
                    </button>
                  </>
                )}
              </span>
            </div>
          ) : userKybInfo.Status !== "Approved" ? (
            <div className="breadcrumbs2">
              <span
                className="kyb-link"
                style={{
                  cursor: "pointer",
                  textDecoration: "none",
                }}
              >
                KYB is under review.{" "}
                <button
                  className="export-btn"
                  onClick={() => navigate("/buyer/setting")}
                >
                  View Here
                </button>
              </span>
            </div>
          ) : null}

          <nav>
            <ul>
              <li className="profile-menu">
                <div
                  className="profile-trigger"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  <Badge
                    badgeContent={
                      userMessageCount > 0 ? userMessageCount : null
                    }
                    color="error"
                    onClick={redirectToMessage}
                    style={{
                      marginRight: "15px",
                      cursor: "pointer",
                    }}
                  >
                    <MessageIcon
                      style={{ width: "20px", color: "#4c4949da" }}
                    />
                  </Badge>

                  <div
                    style={{
                      marginRight: "15px",
                      display: "flex",
                      alignItems: "center",
                      position: "relative",
                    }}
                  >
                    <NotificationCenter size="medium" />
                  </div>

                  {/* Help Dropdown */}
                  <div
                    style={{ position: "relative", marginRight: "15px" }}
                    onClick={() => setIsHelpDropdownVisible((prev) => !prev)}
                  >
                    <HelpOutlineIcon
                      style={{
                        width: "22px",
                        color: "#4c4949da",
                        cursor: "pointer",
                      }}
                    />
                    {isHelpDropdownVisible && (
                      <div
                        style={{
                          position: "absolute",
                          top: "35px",
                          right: 0,
                          background: "#fff",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
                          width: "280px",
                          zIndex: 9999,
                          padding: "6px 0",
                        }}
                      >
                        {menuItems.map((item, index) => {
                          const isExternal =
                            item.link?.startsWith("http") ||
                            item.link?.startsWith("mailto");

                          return isExternal ? (
                            <a
                              key={index}
                              href={item.link}
                              target={
                                item.title === "WhatsApp" ? "_blank" : "_self"
                              }
                              rel="noopener noreferrer"
                              style={{
                                textDecoration: "none",
                                color: "#333",
                                display: "flex",
                                alignItems: "flex-start",
                                padding: "10px 15px",
                                gap: "10px",
                                transition: "background 0.2s",
                                fontWeight: 400,
                                borderRadius: "6px",
                              }}
                              onMouseOver={(e) =>
                                (e.currentTarget.style.background = "#f9f9f9")
                              }
                              onMouseOut={(e) =>
                                (e.currentTarget.style.background =
                                  "transparent")
                              }
                              onClick={() => setIsHelpDropdownVisible(false)}
                            >
                              <div>{item.icon}</div>
                              <div>
                                <div
                                  style={{ fontWeight: 600, fontSize: "14px" }}
                                >
                                  {item.title}
                                </div>
                                {item.subtitle && (
                                  <div
                                    style={{ fontSize: "12px", color: "#777" }}
                                  >
                                    {item.subtitle}
                                  </div>
                                )}
                              </div>
                            </a>
                          ) : (
                            <Link
                              key={index}
                              to={item.link || "#"}
                              style={{
                                textDecoration: "none",
                                color: "#333",
                                display: "flex",
                                alignItems: "flex-start",
                                padding: "10px 15px",
                                gap: "10px",
                                transition: "background 0.2s",
                                fontWeight: 400,
                                borderRadius: "6px",
                              }}
                              onMouseOver={(e) =>
                                (e.currentTarget.style.background = "#f9f9f9")
                              }
                              onMouseOut={(e) =>
                                (e.currentTarget.style.background =
                                  "transparent")
                              }
                              onClick={(e) => {
                                setIsHelpDropdownVisible(false);
                                if (item.title === "Help") {
                                  e.preventDefault();
                                  openHelpModal();
                                }
                              }}
                            >
                              <div>{item.icon}</div>
                              <div>
                                <div
                                  style={{ fontWeight: 600, fontSize: "14px" }}
                                >
                                  {item.title}
                                </div>
                                {item.subtitle && (
                                  <div
                                    style={{ fontSize: "12px", color: "#777" }}
                                  >
                                    {item.subtitle}
                                  </div>
                                )}
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div
                    onClick={toggleDropdown}
                    style={{
                      marginLeft: "10px",
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <PersonIcon style={{ width: "20px", color: "#4c4949da" }} />
                    {activeUser}
                    {isDropdownVisible ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </div>
                </div>
                {isDropdownVisible && (
                  <div className="profile-dropdown">
                    <div className="dropdown-content">
                      <button onClick={goToProfile} className="dropdown-item">
                        <PeopleIcon /> Profile
                      </button>
                      <div className="border-1"></div>
                      {clientmenu ? (
                        <>
                          <button onClick={clientnav} className="dropdown-item">
                            <PersonIcon /> My Client
                          </button>
                          <div className="border-1"></div>
                        </>
                      ) : (
                        ""
                      )}
                      <button onClick={handleSubmit} className="dropdown-item">
                        <ExitToAppIcon /> Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </li>
            </ul>
          </nav>
        </div>
      </header>

      {/* Help Modal */}
      {isHelpModalVisible && (
        <HelpModal
          onClose={() => setIsHelpModalVisible(false)}
          userType="buyer"
        />
      )}

      {/* Inline CSS */}
      <style>
        {`
          .submit-btn-more1 {
            background-color: #f0f0f0;
            color: #333;
            padding: 8px 16px;
            border: none;
            border-radius: 22px;
            cursor: pointer;
            margin: 0 5px;
            position: relative;
          }

          .submit-btn-more1.active {
            background-color: #de6b14;
            color: #fff;
          }

          .custom-tooltip {
            position: absolute;
            top: 40px;
            left: 50%;
            transform: translateX(-50%);
            background-color: #de6b14;
            color: white;
            padding: 6px 10px;
            border-radius: 6px;
            font-size: 13px;
            font-weight: 500;
            white-space: nowrap;
            zIndex: 9999;
            box-shadow: 0 4px 10px rgba(0,0,0,0.15);
          }

          .custom-tooltip::after {
            content: "";
            position: absolute;
            bottom: -6px;
            left: 50%;
            transform: translateX(-50%);
            border-width: 6px;
            border-style: solid;
            border-color: #de6b14 transparent transparent transparent;
          }
        `}
      </style>
    </div>
  );
};

export default Header;
